import React, { useState } from 'react';
import { CONTENTFUL_CONFIG } from '../config/contentfulConfig';

const UserRegistration = () => {
    const [formData, setFormData] = useState({
        idNumber: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        passportNo: '',
        visaNumber: '',
        visaIssueDate: '',
        visaExpiry: '',
        dateTime: '',
        training: '',
        accommodationAddress: '',
        nationality: ''
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null); // Store raw file for upload
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [countries, setCountries] = useState([]);

    React.useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch('https://restcountries.com/v3.1/all?fields=name');
                const data = await response.json();
                const countryNames = data.map(country => country.name.common).sort();
                setCountries(countryNames);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        fetchCountries();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file); // Store file for proper upload
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Helper to Upload, Create, Process, and Publish Image Asset
    const uploadImageToContentful = async (file) => {
        try {
            // Step 1: Upload binary file
            const uploadRes = await fetch(`https://upload.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/uploads`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}`,
                    'Content-Type': 'application/octet-stream'
                },
                body: file
            });
            const uploadData = await uploadRes.json();
            const uploadId = uploadData.sys.id;

            // Step 2: Create Asset
            const assetRes = await fetch(`https://api.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/assets`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}`,
                    'Content-Type': 'application/vnd.contentful.management.v1+json'
                },
                body: JSON.stringify({
                    fields: {
                        title: { 'en-US': `User Image - ${Date.now()}` },
                        file: {
                            'en-US': {
                                contentType: file.type,
                                fileName: file.name,
                                uploadFrom: {
                                    sys: { type: 'Link', linkType: 'Upload', id: uploadId }
                                }
                            }
                        }
                    }
                })
            });
            const assetData = await assetRes.json();
            const assetId = assetData.sys.id;
            const assetVersion = assetData.sys.version;

            // Step 3: Process Asset
            await fetch(`https://api.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/assets/${assetId}/files/en-US/process`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}`,
                    'X-Contentful-Version': assetVersion
                }
            });

            // Wait for processing (simple delay)
            await new Promise(r => setTimeout(r, 2000));

            // Step 4: Publish Asset (Need to fetch latest version first)
            const latestAssetRes = await fetch(`https://api.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/assets/${assetId}`, {
                headers: { 'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}` }
            });
            const latestAssetData = await latestAssetRes.json();
            
            await fetch(`https://api.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/assets/${assetId}/published`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}`,
                    'X-Contentful-Version': latestAssetData.sys.version
                }
            });

            return assetId;
        } catch (error) {
            console.error('Image Upload Failed:', error);
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmissionStatus('submitting');
        setErrorMessage('');

        let imageAssetId = null;

        // Upload Image if selected
        if (imageFile) {
            // Update UI to show image upload status? Optional.
            imageAssetId = await uploadImageToContentful(imageFile);
            if (!imageAssetId) {
                setSubmissionStatus('error');
                setErrorMessage('Image upload failed. Please try again.');
                return;
            }
        }

        const entryData = {
            fields: {
                firstName: { 'en-US': formData.firstName },
                lastName: { 'en-US': formData.lastName },
                idNumber: { 'en-US': formData.idNumber },
                passportNo: { 'en-US': formData.passportNo },
                dateOfBirth: { 'en-US': formData.dateOfBirth },
                nationality: { 'en-US': formData.nationality },
                visaNumber: { 'en-US': formData.visaNumber },
                visaIssueDate: { 'en-US': formData.visaIssueDate },
                visaExpiry: { 'en-US': formData.visaExpiry },
                address: { 'en-US': formData.training }, // 'Training' field has ID 'address' in Contentful
                // dateTime: { 'en-US': formData.dateTime }, // Removed: No field for Date & Time in Content Model
                accommodationAddress: { 'en-US': formData.accommodationAddress },
                image: imageAssetId ? { 'en-US': { sys: { type: 'Link', linkType: 'Asset', id: imageAssetId } } } : undefined
            }
        };

        try {
            const response = await fetch(`https://api.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/entries`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}`,
                    'Content-Type': 'application/vnd.contentful.management.v1+json',
                    'X-Contentful-Content-Type': CONTENTFUL_CONFIG.CONTENT_TYPE_ID
                },
                body: JSON.stringify(entryData)
            });

            if (response.ok) {
                const newEntry = await response.json();
                const entryId = newEntry.sys.id;
                const entryVersion = newEntry.sys.version;

                // Step 2: Publish the Entry
                const publishResponse = await fetch(`https://api.contentful.com/spaces/${CONTENTFUL_CONFIG.SPACE_ID}/environments/master/entries/${entryId}/published`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${CONTENTFUL_CONFIG.MANAGEMENT_TOKEN}`,
                        'X-Contentful-Version': entryVersion,
                        'X-Contentful-Content-Type': CONTENTFUL_CONFIG.CONTENT_TYPE_ID
                    }
                });

                if (publishResponse.ok) {
                    setSubmissionStatus('success');
                    setFormData({
                        idNumber: '',
                        firstName: '',
                        lastName: '',
                        dateOfBirth: '',
                        passportNo: '',
                        visaNumber: '',
                        visaIssueDate: '',
                        visaExpiry: '',
                        dateTime: '',
                        training: '',
                        accommodationAddress: '',
                        nationality: ''
                    });
                    setImagePreview(null);
                    setImageFile(null);
                } else {
                    console.error('Publish Error:', await publishResponse.json());
                    setSubmissionStatus('error');
                    setErrorMessage('Entry created but failed to publish. Check Drafts.');
                }
            } else {
                const errorData = await response.json();
                console.error('Contentful Submission Error:', errorData);
                setSubmissionStatus('error');
                if (errorData.sys && errorData.sys.id === 'OrganizationAccessGrantRequired') {
                     setErrorMessage('Organization Access Missing. Go to Contentful > Settings > API Keys > Content Management Tokens > Click your token > Click "Grant Access".');
                } else if (errorData.message) {
                    setErrorMessage(errorData.message);
                } else {
                    setErrorMessage('Failed to submit data. Check console for details.');
                }
            }
        } catch (error) {
            console.error('Network Error:', error);
            setSubmissionStatus('error');
        }
    };

    // Inline Styles for Premium Look
    const styles = {
        sectionHeader: {
            borderBottom: '2px solid #fab909',
            paddingBottom: '10px',
            marginBottom: '20px',
            color: '#020e2a',
            fontSize: '1.2rem',
            fontWeight: '600',
            textTransform: 'uppercase'
        },
        card: {
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            padding: '20px', // Reduced from 30px
            marginBottom: '20px' // Reduced from 30px
        },
        uploadBox: {
            border: '2px dashed #fab909',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#fdfdfd',
            transition: 'all 0.3s ease',
            minHeight: '180px', // Slightly reduced
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        },
        previewImg: {
            maxWidth: '100%',
            maxHeight: '160px',
            borderRadius: '5px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        },
        inputField: {
            width: '100%',
            padding: '8px 12px', // Compact padding
            margin: '5px 0',    // Compact margin
            display: 'inline-block',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxSizing: 'border-box',
            transition: 'border-color 0.3s ease',
            backgroundColor: '#f9f9f9',
            fontSize: '14px' // Slightly smaller font for compact look
        },
        inputLabel: {
            fontWeight: '600',
            color: '#555',
            marginBottom: '3px', // Compact margin
            display: 'block',
            fontSize: '14px'
        },
        submitBtn: {
            width: '100%',
            maxWidth: '300px',
            fontSize: '16px',
            padding: '12px',
            backgroundColor: '#fab909', // Theme Gold
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            fontWeight: 'bold',
            cursor: 'pointer',
            textTransform: 'uppercase',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            transition: 'background 0.3s ease'
        }
    };

    return (
        <section className="contact-area pb-100 pt-100" style={{ backgroundColor: '#f4f6f9' }}>
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="section-title text-center mb-50">
                            <h2>New User Registration</h2>
                            <p>Securely onboard new personnel into Iron Wall X</p>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <form onSubmit={handleSubmit}>
                            
                            {/* Section 1: Identity & Photo */}
                            <div style={styles.card}>
                                <h3 style={styles.sectionHeader}>1. Identity Verification</h3>
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label style={styles.inputLabel}>Profile Photo</label>
                                            <div style={styles.uploadBox} onClick={() => document.getElementById('fileInput').click()}>
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" style={styles.previewImg} />
                                                ) : (
                                                    <div>
                                                        <i className="fa fa-cloud-upload" style={{ fontSize: '40px', color: '#fab909', marginBottom: '10px' }}></i>
                                                        <p style={{ margin: 0, color: '#777' }}>Click to Upload Image</p>
                                                    </div>
                                                )}
                                                <input 
                                                    id="fileInput" 
                                                    type="file" 
                                                    accept="image/*" 
                                                    onChange={handleImageChange} 
                                                    style={{ display: 'none' }} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="single-input-field">
                                                    <label style={styles.inputLabel}>Passport Number</label>
                                                    <input type="text" name="passportNo" placeholder="Passport Number" value={formData.passportNo} onChange={handleChange} style={styles.inputField} required />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="single-input-field">
                                                    <label style={styles.inputLabel}>National ID / Iqama</label>
                                                    <input type="text" name="idNumber" placeholder="ID Number" value={formData.idNumber} onChange={handleChange} style={styles.inputField} />
                                                </div>
                                            </div>
                                            <div className="col-md-12 mt-3">
                                                <div className="alert alert-warning" style={{ fontSize: '0.85rem', padding: '10px' }}>
                                                    <i className="fa fa-info-circle"></i> Ensure Passport Number matches the physical document.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Personal Details */}
                            <div style={styles.card}>
                                <h3 style={styles.sectionHeader}>2. Personal Details</h3>
                                <div className="row">
                                    <div className="col-lg-6 col-md-6">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>First Name</label>
                                            <input type="text" name="firstName" placeholder="Enter First Name" value={formData.firstName} onChange={handleChange} style={styles.inputField} required />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Last Name</label>
                                            <input type="text" name="lastName" placeholder="Enter Last Name" value={formData.lastName} onChange={handleChange} style={styles.inputField} required />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Date of Birth</label>
                                            <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} style={styles.inputField} required />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Nationality</label>
                                            <select name="nationality" value={formData.nationality} onChange={handleChange} style={styles.inputField}>
                                                <option value="">Select Nationality</option>
                                                {countries.map((country, index) => (
                                                    <option key={index} value={country}>{country}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Visa Verification */}
                            <div style={styles.card}>
                                <h3 style={styles.sectionHeader}>3. Visa Information</h3>
                                <div className="row">
                                    <div className="col-lg-4 col-md-4">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Visa Number</label>
                                            <input type="text" name="visaNumber" placeholder="Visa Number" value={formData.visaNumber} onChange={handleChange} style={styles.inputField} />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-4">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Issue Date</label>
                                            <input type="date" name="visaIssueDate" value={formData.visaIssueDate} onChange={handleChange} style={styles.inputField} />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 col-md-4">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Expiry Date</label>
                                            <input type="date" name="visaExpiry" value={formData.visaExpiry} onChange={handleChange} style={styles.inputField} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Assignment */}
                            <div style={styles.card}>
                                <h3 style={styles.sectionHeader}>4. Assignment & Contact</h3>
                                <div className="row">
                                    <div className="col-lg-6 col-md-6">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Training Program</label>
                                            <input type="text" name="training" placeholder="e.g. Basic Security Training" value={formData.training} onChange={handleChange} style={styles.inputField} />
                                        </div>
                                    </div>
                                    <div className="col-lg-6 col-md-6">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Start Date & Time</label>
                                            <input type="datetime-local" name="dateTime" value={formData.dateTime} onChange={handleChange} style={styles.inputField} />
                                        </div>
                                    </div>
                                    <div className="col-lg-12">
                                        <div className="single-input-field">
                                            <label style={styles.inputLabel}>Accommodation Address</label>
                                            <textarea name="accommodationAddress" placeholder="Full Address" value={formData.accommodationAddress} onChange={handleChange} rows="3" style={styles.inputField}></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Action */}
                            <div className="row">
                                <div className="col-lg-12 text-center">
                                    <div className="single-input-fieldsbtn">
                                        <button type="submit" style={styles.submitBtn}>
                                            Submit Registration
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {submissionStatus === 'submitting' && (
                                <div className="alert alert-info mt-4 text-center">
                                    <i className="fa fa-spinner fa-spin"></i> Uploading Image & Submitting data...
                                </div>
                            )}

                            {submissionStatus === 'success' && (
                                <div className="alert alert-success mt-4 text-center">
                                    <strong>Success!</strong> Registration data has been <strong>Published</strong> to Contentful.
                                </div>
                            )}

                            {submissionStatus === 'error' && (
                                <div className="alert alert-danger mt-4 text-center">
                                    <strong>Error!</strong> {errorMessage}
                                </div>
                            )}

                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserRegistration;
